'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridPerspectiveBottomProps {
  /**
   * Enable entrance animation on load
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridPerspectiveBottom - 3D perspective grid receding toward the bottom
 *
 * Creates a dramatic floor grid effect that recedes toward the bottom of
 * the container. Retro/80s/cyberpunk aesthetic with vanishing point.
 *
 * Fixed settings: depth 200, cellSize 40
 *
 * @example
 * ```tsx
 * <section className="relative overflow-hidden">
 *   <GridPerspectiveBottom animate />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridPerspectiveBottom({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridPerspectiveBottomProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const depth = 200
  const cellSize = 40

  const gridStyle = {
    position: 'absolute' as const,
    bottom: 0,
    left: '-50%',
    width: '200%',
    height: '200%',
    backgroundImage: `
      linear-gradient(to right, color-mix(in srgb, var(--border), transparent 25%) 1px, transparent 1px),
      linear-gradient(to bottom, color-mix(in srgb, var(--border), transparent 25%) 1px, transparent 1px)
    `,
    backgroundSize: `${cellSize}px ${cellSize}px`,
    transform: 'rotateX(75deg)',
    transformOrigin: 'center bottom',
    maskImage: 'linear-gradient(to top, black 0%, black 50%, transparent 90%)',
    WebkitMaskImage: 'linear-gradient(to top, black 0%, black 50%, transparent 90%)',
  }

  if (!animate) {
    return (
      <div
        className={cn('absolute inset-0 z-0 overflow-hidden', className)}
        style={{ perspective: `${depth}px` }}
      >
        <div style={gridStyle} />
      </div>
    )
  }

  // Animated version: grid rises up from the horizon
  return (
    <div
      className={cn('absolute inset-0 z-0 overflow-hidden', className)}
      style={{ perspective: `${depth}px` }}
    >
      <motion.div
        style={gridStyle}
        initial={{
          opacity: 0,
          transform: 'rotateX(85deg) translateY(50%)',
        }}
        animate={{
          opacity: 1,
          transform: 'rotateX(75deg) translateY(0%)',
        }}
        transition={{
          duration: prefersReducedMotion ? 0.3 : 1.5,
          delay: prefersReducedMotion ? 0 : animationDelay,
          ease: [0.16, 1, 0.3, 1],
        }}
      />
    </div>
  )
}
